import GeneralService from "./GeneralService";
import { ApiKeyOrGetter } from "./GeneralService";

export interface TagDefinition {
  id: number;
  company_id: number;
  intent_group_id?: number;
  name: string;
  description?: string;
  scope: string;
  rule_type: string;
  rule_config: Record<string, unknown>;
  color?: string;
  is_base: boolean;
  active: boolean;
  date_created: string;
  date_updated: string;
  meta: Record<string, unknown>;
}

export interface TagRun {
  id: number;
  tag_definition_id: number;
  status: string;
  total_requests: number;
  processed_requests: number;
  matched_requests: number;
  date_created: string;
  meta: Record<string, unknown>;
}

export interface TagAssignment {
  id: number;
  tag_definition_id: number;
  request_id?: string;
  session_id?: string;
  confidence?: number;
  date_created: string;
}

type LoadingHandler = ((val: boolean) => void) | undefined;

export interface TagRunPayload {
  date_start?: string;
  date_end?: string;
  meta?: Record<string, unknown>;
}

export interface TagDefinitionPayload {
  name: string;
  description?: string;
  scope?: string;
  rule_type: string;
  rule_config: Record<string, unknown>;
  color?: string;
  intent_group_id?: number;
}

export class TagService {
  public static list(
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter,
    intentGroupId?: number
  ) {
    const params = intentGroupId
      ? `?intent_group_id=${intentGroupId}`
      : "";
    return GeneralService.get(
      `/tags/${params}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static get(
    tagId: number,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/tags/${tagId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static create(
    payload: TagDefinitionPayload,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/tags/",
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static update(
    tagId: number,
    payload: Partial<TagDefinitionPayload>,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/tags/${tagId}`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteTag(
    tagId: number,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/tags/${tagId}`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static triggerRun(
    tagId: number,
    payload: TagRunPayload,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/tags/${tagId}/run`,
      JSON.stringify(payload || {}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static listRuns(
    tagId: number,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/tags/${tagId}/runs`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getRun(
    runId: number,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/tags/runs/${runId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static preview(
    payload: TagDefinitionPayload,
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/tags/preview",
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getAssignments(
    params: { request_ids?: string; session_ids?: string; tag_definition_ids?: string },
    loadingHandler: LoadingHandler,
    apiKey: ApiKeyOrGetter
  ) {
    const query = new URLSearchParams();
    if (params.request_ids) query.set("request_ids", params.request_ids);
    if (params.session_ids) query.set("session_ids", params.session_ids);
    if (params.tag_definition_ids)
      query.set("tag_definition_ids", params.tag_definition_ids);
    return GeneralService.get(
      `/tags/assignments?${query.toString()}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
